/**
 * Default email templates that can be bulk-created for an organization.
 * Uses @ parameters that get replaced at send time via fillVariablesInEmailTemplate.
 *
 * Available variables:
 *   @contacts       — All client contacts (e.g. "M. Dupont, Mme. Dupont")
 *   @nom_projet     — Project name
 *   @adresse_projet — Project address + city
 *   @signature      — Current user name + org name
 */

export interface DefaultEmailTemplate {
  name: string;
  subject: string;
  body: string;
  destination_type: "client" | "partner";
  mandatory_photo?: boolean;
  library_documents_id?: string[];
}

export const defaultEmailTemplates: DefaultEmailTemplate[] = [
  {
    name: "Confirmation de rendez-vous",
    subject: "Confirmation de rendez-vous — @nom_projet",
    destination_type: "client",
    body: `Bonjour @contacts,

Je vous confirme notre rendez-vous du [ compléter la date ] à [ compléter l'heure ] qui aura lieu à @adresse_projet.

Je reste bien entendu à votre disposition en cas d'empêchement ou de modification.

Bonne journée.

@signature`,
  },
  {
    name: "Validation des plans d'exécution",
    subject: "Validation des plans d'exécution — @nom_projet",
    destination_type: "client",
    body: `Bonjour @contacts,

Vous trouverez ci-joints vos plans d'exécution pour validation.

Nous vous remercions de bien vouloir les examiner et nous retourner les plans signés si ceux-là vous conviennent.

Je reste bien entendu à votre disposition pour toute question ou demande de précision.

@signature`,
  },
  {
    name: "Confirmation chantier prêt",
    subject: "Chantier prêt — @nom_projet",
    destination_type: "partner",
    body: `Bonjour,

Le chantier de @contacts, situé @adresse_projet, est prêt pour votre intervention.

Vous pouvez intervenir à partir du [ compléter la date ].

Merci de me confirmer votre date d'intervention.

Je reste disponible si besoin.

Bonne journée.

@signature`,
  },
  {
    name: "Information retard chantier",
    subject: "Retard chantier — @nom_projet",
    destination_type: "partner",
    body: `Bonjour,

Pour information, le chantier de @contacts, situé @adresse_projet, a pris du retard.

Merci de bien vouloir décaler votre intervention à partir du [ compléter la date ].

Je vous remercie de me confirmer votre nouvelle date de passage.

Bonne journée.

@signature`,
  },
  {
    name: "Ouverture compteur d'eau",
    subject: "Ouverture compteur d'eau — @nom_projet",
    destination_type: "client",
    body: `Bonjour @contacts,

Nous vous invitons à procéder à l'ouverture de votre compteur d'eau si cela n'est pas déjà fait.

Il vous suffit de contacter le fournisseur d'eau de votre commune.

Vous trouverez le document ci-joint.

Bonne journée.

@signature`,
  },
  {
    name: "Pose du compteur provisoire",
    subject: "Compteur provisoire — @nom_projet",
    destination_type: "client",
    body: `Bonjour @contacts,

Nous vous invitons à procéder à l'installation de votre compteur de chantier si cela n'est pas déjà fait.

Les artisans auront besoin de l'électricité pour pouvoir réaliser les travaux.

Vous pouvez consulter ci-joint les instructions pour obtenir l'alimentation électrique de votre compteur de chantier.

Bonne journée.

@signature`,
  },
  {
    name: "Rappel dépôt de la DROC",
    subject: "Rappel DROC — @nom_projet",
    destination_type: "client",
    body: `Bonjour @contacts,

Votre chantier vient de débuter.

Vous devez donc procéder à votre déclaration d'ouverture de chantier (DROC).

Bonne journée.

@signature`,
  },
  {
    name: "Livraison de la charpente",
    subject: "Livraison charpente — @nom_projet",
    destination_type: "partner",
    body: `Bonjour,

Merci de procéder à la livraison de la charpente pour le chantier @nom_projet situé @adresse_projet.

Merci d'avance.

Bonne journée.

@signature`,
  },
  {
    name: "Livraison des coffres de VR",
    subject: "Livraison coffres VR — @nom_projet",
    destination_type: "partner",
    body: `Bonjour,

Merci de procéder à la livraison des coffres de volets roulants pour le chantier @nom_projet situé @adresse_projet.

Merci d'avance.

Bonne journée.

@signature`,
  },
  {
    name: "Réseaux sous dalle",
    subject: "Intervention réseaux sous dalle — @nom_projet",
    destination_type: "partner",
    mandatory_photo: true,
    body: `Bonjour,

Merci de prévoir l'intervention des réseaux sous dalle pour le chantier @nom_projet situé @adresse_projet pour le [ compléter la date ].

Merci de me confirmer votre date d'intervention.

Je reste disponible si besoin.

Bonne journée.

@signature`,
  },
  {
    name: "Livraison des coffres",
    subject: "Livraison coffres — @nom_projet",
    destination_type: "partner",
    mandatory_photo: true,
    body: `Bonjour,

Merci de procéder à la livraison des coffres pour le chantier @nom_projet situé @adresse_projet pour le [ compléter la date ].

Merci de me confirmer votre date d'intervention.

Je reste disponible si besoin.

Bonne journée.

@signature`,
  },
  {
    name: "Traitement anti-termites",
    subject: "Traitement anti-termites — @nom_projet",
    destination_type: "partner",
    mandatory_photo: true,
    body: `Bonjour,

La dalle du chantier @nom_projet situé @adresse_projet est coulée.

Merci de prévoir votre intervention pour le [ compléter la date ].

Merci de me confirmer votre date d'intervention.

Je reste disponible si besoin.

Bonne journée.

@signature`,
  },
  {
    name: "Prêt pour la couverture",
    subject: "Chantier prêt pour la couverture — @nom_projet",
    destination_type: "partner",
    mandatory_photo: true,
    body: `Bonjour,

Le chantier de @contacts, situé @adresse_projet, est prêt pour la pose de la couverture.

Vous pouvez intervenir à partir du [ compléter la date ].

Merci de me confirmer votre date d'intervention.

Je reste disponible si besoin.

Bonne journée.

@signature`,
  },
  {
    name: "Prêt pour les menuiseries extérieures",
    subject: "Chantier prêt pour les menuiseries — @nom_projet",
    destination_type: "partner",
    mandatory_photo: true,
    body: `Bonjour,

Le chantier de @contacts, situé @adresse_projet, est prêt pour la pose des menuiseries extérieures.

Vous pouvez intervenir à partir du [ compléter la date ].

Merci de me confirmer votre date d'intervention.

Je reste disponible si besoin.

Bonne journée.

@signature`,
  },
  {
    name: "Maison hors d'eau hors d'air",
    subject: "Maison hors d'eau hors d'air — @nom_projet",
    destination_type: "client",
    body: `Bonjour @contacts,

Votre maison sitée @adresse_projet est désormais hors d'eau et hors d'air.

À ce stade, vous devez impérativement procéder à l'assurance de votre bien si ce n'est pas déjà fait.

N'hésitez pas à nous contacter pour toute question.

Bonne journée.

@signature`,
  },
  {
    name: "Prêt pour l'enduit",
    subject: "Chantier prêt pour l'enduit — @nom_projet",
    destination_type: "partner",
    mandatory_photo: true,
    body: `Bonjour,

Le chantier de @contacts, situé @adresse_projet, est prêt pour la réalisation de l'enduit.

Vous pouvez intervenir à partir du [ compléter la date ].

Merci de me confirmer votre date d'intervention.

Je reste disponible si besoin.

Bonne journée.

@signature`,
  },
  {
    name: "Raccordement électricité définitif",
    subject: "Demande de raccordement électrique — @nom_projet",
    destination_type: "client",
    body: `Bonjour @contacts,

Le chantier de @nom_projet situé @adresse_projet avance bien.

À ce stade, vous devez réaliser votre demande de raccordement électrique définitif auprès d'Enedis.

C'est également le bon moment pour finaliser vos choix de carrelage et de faïence.

N'hésitez pas à nous contacter si vous avez des questions.

Bonne journée.

@signature`,
  },
  {
    name: "Demande du CONSUEL",
    subject: "Demande CONSUEL — @nom_projet",
    destination_type: "partner",
    body: `Bonjour,

Merci de réaliser la demande de CONSUEL pour le chantier de @contacts situé @adresse_projet dès que possible.

Je reste disponible si besoin.

Bonne journée.

@signature`,
  },
  {
    name: "Pose du bac à douche / baignoire",
    subject: "Planification bac à douche — @nom_projet",
    destination_type: "partner",
    mandatory_photo: true,
    body: `Bonjour,

Merci de planifier la pose du bac à douche / baignoire pour le chantier de @contacts situé @adresse_projet dès que possible.

Je reste disponible si besoin.

Bonne journée.

@signature`,
  },
  {
    name: "Dépôt de la DAACT",
    subject: "Dépôt de la DAACT — @nom_projet",
    destination_type: "client",
    body: `Bonjour @contacts,

Votre chantier situé @adresse_projet est désormais terminé.

Vous devez procéder au dépôt de votre Déclaration Attestant l'Achèvement et la Conformité des Travaux (DAACT) auprès de votre mairie.

Félicitations pour votre nouvelle maison !

Bonne journée.

@signature`,
  },
];
